/**
 * 客户管理页面
 * 仅管理员可用：注册客户、查看列表、绑定用户名、设置VIP、删除客户
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Avatar, Space, Button, Modal, Select,
  Input, Form, message, Popconfirm, Tooltip, Statistic, Row, Col,
} from 'antd';
import {
  UserOutlined, SearchOutlined, CrownOutlined,
  PhoneOutlined, EnvironmentOutlined, PlusOutlined,
  LinkOutlined, DeleteOutlined, KeyOutlined, MergeCellsOutlined, EditOutlined,
} from '@ant-design/icons';
import { customerApi, vipLevelApi } from '../services/api';

// 注册来源映射
const sourceMap = {
  wechat:  { label: '微信',   color: '#07c160', icon: '📱' },
  alipay:  { label: '支付宝', color: '#1677ff', icon: '💳' },
  phone:   { label: '手机号', color: '#fa8c16', icon: '📞' },
};

const CustomerManage = () => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [filterVipId, setFilterVipId] = useState(null);
  const [vipLevels, setVipLevels] = useState([]);

  // VIP设置弹窗
  const [vipModalOpen, setVipModalOpen] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [selectedVipId, setSelectedVipId] = useState(null);
  const [vipLoading, setVipLoading] = useState(false);

  // 注册客户弹窗
  const [regModalOpen, setRegModalOpen] = useState(false);
  const [regForm] = Form.useForm();
  const [regLoading, setRegLoading] = useState(false);

  // 绑定用户名弹窗
  const [bindModalOpen, setBindModalOpen] = useState(false);
  const [bindForm] = Form.useForm();
  const [bindLoading, setBindLoading] = useState(false);
  const [bindCustomer, setBindCustomer] = useState(null);

  // 重置密码弹窗
  const [resetPwdModalOpen, setResetPwdModalOpen] = useState(false);
  const [resetPwdForm] = Form.useForm();
  const [resetPwdLoading, setResetPwdLoading] = useState(false);
  const [resetPwdCustomer, setResetPwdCustomer] = useState(null);

  // 编辑客户信息弹窗
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  // 加载VIP等级列表
  const loadVipLevels = useCallback(async () => {
    try {
      const res = await vipLevelApi.list();
      setVipLevels(res.data || []);
    } catch { }
  }, []);

  // 加载客户列表
  const loadList = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await customerApi.list({
        page, pageSize, keyword: keyword || undefined,
        vipLevelId: filterVipId || undefined,
      });
      setList(res.data.list || []);
      setPagination(res.data.pagination);
    } catch { }
    finally { setLoading(false); }
  }, [keyword, filterVipId]);

  useEffect(() => {
    loadVipLevels();
  }, [loadVipLevels]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // 搜索
  const handleSearch = () => {
    loadList(1, pagination.pageSize);
  };

  // 表格分页变化
  const handleTableChange = (pag) => {
    loadList(pag.current, pag.pageSize);
  };

  // ==================== VIP 操作 ====================
  const openVipModal = (record) => {
    setCurrentCustomer(record);
    setSelectedVipId(record.vip_level_id || null);
    setVipModalOpen(true);
  };

  const handleVipSave = async () => {
    setVipLoading(true);
    try {
      await customerApi.setVip(currentCustomer.id, selectedVipId);
      message.success('VIP等级设置成功');
      setVipModalOpen(false);
      loadList(pagination.page, pagination.pageSize);
    } catch { }
    finally { setVipLoading(false); }
  };

  const handleCancelVip = async (record) => {
    try {
      await customerApi.setVip(record.id, null);
      message.success('已取消VIP等级');
      loadList(pagination.page, pagination.pageSize);
    } catch { }
  };

  // ==================== 注册客户 ====================
  const openRegModal = () => {
    regForm.resetFields();
    setRegModalOpen(true);
  };

  const handleRegCustomer = async () => {
    try {
      const values = await regForm.validateFields();
      setRegLoading(true);
      await customerApi.create(values);
      message.success('客户注册成功');
      setRegModalOpen(false);
      regForm.resetFields();
      loadList(1, pagination.pageSize);
    } catch (err) {
      if (err.errorFields) return;
    } finally {
      setRegLoading(false);
    }
  };

  // ==================== 绑定用户名 ====================
  const openBindModal = (record) => {
    setBindCustomer(record);
    bindForm.resetFields();
    setBindModalOpen(true);
  };

  const handleBindSave = async () => {
    try {
      const values = await bindForm.validateFields();
      setBindLoading(true);
      await customerApi.bind(bindCustomer.id, values);
      message.success('用户名绑定成功');
      setBindModalOpen(false);
      bindForm.resetFields();
      loadList(pagination.page, pagination.pageSize);
    } catch (err) {
      if (err.errorFields) return;
    } finally {
      setBindLoading(false);
    }
  };

  // ==================== 重置密码 ====================
  const openResetPwdModal = (record) => {
    setResetPwdCustomer(record);
    resetPwdForm.resetFields();
    setResetPwdModalOpen(true);
  };

  const handleResetPwd = async () => {
    try {
      const values = await resetPwdForm.validateFields();
      setResetPwdLoading(true);
      await customerApi.resetPassword(resetPwdCustomer.id, values.password);
      message.success('密码重置成功');
      setResetPwdModalOpen(false);
      resetPwdForm.resetFields();
    } catch (err) {
      if (err.errorFields) return;
    } finally {
      setResetPwdLoading(false);
    }
  };

  // ==================== 编辑客户信息 ====================
  const openEditModal = (record) => {
    setEditCustomer(record);
    editForm.setFieldsValue({
      nickname: record.nickname || '',
      contactName: record.contact_name || '',
      address: record.address || '',
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      await customerApi.update(editCustomer.id, values);
      message.success('客户信息已更新');
      setEditModalOpen(false);
      editForm.resetFields();
      loadList(pagination.page, pagination.pageSize);
    } catch (err) {
      if (err.errorFields) return;
    } finally {
      setEditLoading(false);
    }
  };

  // ==================== 删除客户 ====================
  const handleDelete = async (record) => {
    try {
      await customerApi.remove(record.id);
      message.success('客户已删除');
      loadList(pagination.page, pagination.pageSize);
    } catch { }
  };

  // ==================== 合并客户（解决一人双号） ====================
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSecondary, setMergeSecondary] = useState(null);
  const [mergePrimaryId, setMergePrimaryId] = useState(null);
  const [mergeLoading, setMergeLoading] = useState(false);

  const openMergeModal = (record) => {
    setMergeSecondary(record);
    setMergePrimaryId(null);
    setMergeModalOpen(true);
  };

  const handleMergeSave = async () => {
    if (!mergePrimaryId) {
      message.warning('请选择要保留的主账号');
      return;
    }
    setMergeLoading(true);
    try {
      await customerApi.merge(mergePrimaryId, mergeSecondary.id);
      message.success('合并成功');
      setMergeModalOpen(false);
      loadList(pagination.page, pagination.pageSize);
    } catch { }
    finally { setMergeLoading(false); }
  };

  // ==================== 表格列定义 ====================
  const columns = [
    {
      title: '客户',
      key: 'customer',
      width: 220,
      render: (_, r) => (
        <Space>
          <Avatar src={r.avatar_url} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>
              {r.nickname || '未命名'}
              {r.username && (
                <Tag color="blue" style={{ marginLeft: 6, fontSize: 11 }}>
                  <KeyOutlined /> {r.username}
                </Tag>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#999' }}>
              {r.contact_name && <span>{r.contact_name} · </span>}
              {sourceMap[r.source] ? (
                <Tag color={sourceMap[r.source].color} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                  {sourceMap[r.source].icon} {sourceMap[r.source].label}
                </Tag>
              ) : null}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '手机号', dataIndex: 'phone', width: 130,
      render: (v) => v ? <Space size={4}><PhoneOutlined style={{ color: '#999' }} />{v}</Space> : '-',
    },
    {
      title: '地址', dataIndex: 'address', ellipsis: true,
      render: (v) => v ? (
        <Tooltip title={v}>
          <Space size={4}><EnvironmentOutlined style={{ color: '#999' }} />{v}</Space>
        </Tooltip>
      ) : '-',
    },
    {
      title: 'VIP等级', dataIndex: 'vip_name', width: 130,
      render: (v, r) => {
        if (!v) return <Tag>未分级</Tag>;
        return (
          <Tag color={r.vip_color || '#666'} icon={<CrownOutlined />}>
            {v} {r.vip_discount < 100 && `(${r.vip_discount}折)`}
          </Tag>
        );
      },
    },
    {
      title: '消费次数', dataIndex: 'order_count', width: 90,
      sorter: (a, b) => (a.order_count || 0) - (b.order_count || 0),
    },
    {
      title: '累计消费', dataIndex: 'total_paid', width: 120,
      render: (v) => v ? `¥${Number(v).toFixed(2)}` : '¥0.00',
      sorter: (a, b) => (a.total_paid || 0) - (b.total_paid || 0),
    },
    {
      title: '注册时间', dataIndex: 'created_at', width: 170,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作', key: 'actions', width: 280, fixed: 'right',
      render: (_, r) => (
        <Space size="small" wrap>
          <Button size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(r)}
          >
            编辑
          </Button>
          <Button size="small" type="primary" ghost
            icon={<CrownOutlined />}
            onClick={() => openVipModal(r)}
          >
            {r.vip_level_id ? '改等级' : '设VIP'}
          </Button>
          {r.vip_level_id && (
            <Popconfirm
              title="确定取消该客户的VIP等级？"
              onConfirm={() => handleCancelVip(r)}
            >
              <Button size="small" danger>取消VIP</Button>
            </Popconfirm>
          )}
          {!r.hasAccount ? (
            <Button size="small"
              icon={<LinkOutlined />}
              onClick={() => openBindModal(r)}
            >
              绑定
            </Button>
          ) : (
            <Button size="small"
              icon={<KeyOutlined />}
              onClick={() => openResetPwdModal(r)}
              style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
            >
              重置密码
            </Button>
          )}
          <Button size="small"
            icon={<MergeCellsOutlined />}
            onClick={() => openMergeModal(r)}
          >
            合并
          </Button>
          {(r.order_count || 0) === 0 ? (
            <Popconfirm
              title="确定删除该客户？此操作不可撤销。"
              description="删除后该客户数据将永久移除。"
              onConfirm={() => handleDelete(r)}
              okText="确认删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          ) : (
            <Tooltip title={`有关联订单(${r.order_count}条)，无法删除`}>
              <Button size="small" danger icon={<DeleteOutlined />} disabled>
                删除
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space wrap>
              <Input
                placeholder="搜索昵称/手机号/联系人/用户名"
                prefix={<SearchOutlined />}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 260 }}
                allowClear
              />
              <Select
                placeholder="VIP等级筛选"
                value={filterVipId}
                onChange={(v) => { setFilterVipId(v); }}
                allowClear
                style={{ width: 160 }}
              >
                {vipLevels.filter(l => l.status === 1).map(l => (
                  <Select.Option key={l.id} value={l.id}>
                    <Space>
                      <Tag color={l.color}>{l.name}</Tag>
                      {l.discount < 100 && `${l.discount}折`}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
              <Button type="primary" onClick={handleSearch}>搜索</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openRegModal}>
                注册客户
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              <Statistic title="客户总数" value={pagination.total} />
              <Statistic
                title="VIP客户"
                value={list.filter(c => c.vip_level_id && c.vip_discount < 100).length}
                suffix={pagination.total > 0 ? ` / ${pagination.total}` : ''}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 位客户`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* ==================== VIP等级设置弹窗 ==================== */}
      <Modal
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            {currentCustomer ? `设置VIP等级 - ${currentCustomer.nickname || currentCustomer.contact_name || '客户'}` : '设置VIP等级'}
          </Space>
        }
        open={vipModalOpen}
        onOk={handleVipSave}
        onCancel={() => { setVipModalOpen(false); }}
        confirmLoading={vipLoading}
        okText="确认设置"
        cancelText="取消"
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="选择VIP等级">
            <Select
              placeholder="请选择VIP等级"
              value={selectedVipId}
              onChange={setSelectedVipId}
              style={{ width: '100%' }}
            >
              <Select.Option value={null}>无等级（原价）</Select.Option>
              {vipLevels.filter(l => l.status === 1).map(l => (
                <Select.Option key={l.id} value={l.id}>
                  <Space>
                    <Tag color={l.color}>{l.name}</Tag>
                    <span>{l.discount < 100 ? `${l.discount}折` : '原价'}</span>
                    {l.description && <span style={{ color: '#999' }}>- {l.description}</span>}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedVipId && (() => {
            const level = vipLevels.find(l => l.id === selectedVipId);
            if (level && level.discount < 100) {
              return (
                <div style={{
                  background: '#fffbe6', border: '1px solid #ffe58f',
                  borderRadius: 6, padding: '12px 16px', marginTop: -8,
                }}>
                  <div style={{ color: '#ad6800', fontSize: 13 }}>
                    该等级客户在维修报价和销售下单时将自动享受
                    <strong style={{ fontSize: 18, margin: '0 4px' }}>
                      {level.discount / 10}折
                    </strong>
                    优惠
                  </div>
                  <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                    例：原价 ¥100.00 → 实付 ¥{(100 * level.discount / 100).toFixed(2)}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </Form>
      </Modal>

      {/* ==================== 注册客户弹窗 ==================== */}
      <Modal
        title={<Space><PlusOutlined />注册客户</Space>}
        open={regModalOpen}
        onOk={handleRegCustomer}
        onCancel={() => { setRegModalOpen(false); regForm.resetFields(); }}
        confirmLoading={regLoading}
        okText="确认注册"
        cancelText="取消"
        destroyOnClose
        width={480}
      >
        <Form form={regForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
            ]}
          >
            <Input placeholder="11位手机号（必填）" maxLength={11} prefix={<PhoneOutlined />} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="用户名（选填）"
                rules={[
                  { pattern: /^[a-zA-Z0-9_\u4e00-\u9fa5]{3,20}$/, message: '3-20位，字母/数字/中文/下划线' },
                ]}
              >
                <Input placeholder="用于账号密码登录" maxLength={20} prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="password"
                label="密码（选填）"
                rules={[
                  { min: 6, message: '密码至少6位' },
                ]}
              >
                <Input.Password placeholder="配合用户名使用" maxLength={32} prefix={<KeyOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="nickname" label="昵称">
            <Input placeholder="客户昵称" maxLength={50} />
          </Form.Item>
          <Form.Item name="contactName" label="联系人">
            <Input placeholder="联系人姓名" maxLength={20} />
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input.TextArea placeholder="客户地址" maxLength={200} rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ==================== 绑定用户名弹窗 ==================== */}
      <Modal
        title={
          <Space>
            <LinkOutlined style={{ color: '#1677ff' }} />
            绑定用户名密码
            {bindCustomer && (
              <Tag>{bindCustomer.nickname || bindCustomer.phone || `ID:${bindCustomer.id}`}</Tag>
            )}
          </Space>
        }
        open={bindModalOpen}
        onOk={handleBindSave}
        onCancel={() => { setBindModalOpen(false); bindForm.resetFields(); }}
        confirmLoading={bindLoading}
        okText="确认绑定"
        cancelText="取消"
        destroyOnClose
        width={420}
      >
        <div style={{
          background: '#e6f4ff', border: '1px solid #91caff',
          borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13,
        }}>
          为微信/支付宝注册的客户绑定用户名密码后，该客户即可通过用户名密码登录。
        </div>
        <Form form={bindForm} layout="vertical">
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { pattern: /^[a-zA-Z0-9_\u4e00-\u9fa5]{3,20}$/, message: '3-20位，字母/数字/中文/下划线' },
            ]}
          >
            <Input placeholder="设置登录用户名" maxLength={20} prefix={<UserOutlined />} />
          </Form.Item>
          <Form.Item
            name="password"
            label="登录密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="设置登录密码" maxLength={32} prefix={<KeyOutlined />} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ==================== 合并客户弹窗 ==================== */}
      <Modal
        title={
          <Space>
            <MergeCellsOutlined style={{ color: '#fa8c16' }} />
            合并客户（解决一人双号）
          </Space>
        }
        open={mergeModalOpen}
        onOk={handleMergeSave}
        onCancel={() => { setMergeModalOpen(false); }}
        confirmLoading={mergeLoading}
        okText="确认合并"
        cancelText="取消"
        destroyOnClose
        width={560}
      >
        <div style={{
          background: '#fff7e6', border: '1px solid #ffd591',
          borderRadius: 6, padding: '12px 14px', marginBottom: 16, fontSize: 13,
        }}>
          <p style={{ margin: 0, color: '#ad6800', fontWeight: 500 }}>⚠️ 合并说明：</p>
          <p style={{ margin: '6px 0 0', color: '#ad6800' }}>
            将 <Tag color="orange">{mergeSecondary?.nickname || mergeSecondary?.phone || '当前客户'}</Tag> 的订单和微信账号合并到主账号。
            合并后当前客户将被删除，数据全部归入主账号。
          </p>
        </div>
        <Form layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="选择要保留的主账号" required>
            <Select
              placeholder="请输入客户昵称或手机号搜索"
              showSearch
              value={mergePrimaryId}
              onChange={setMergePrimaryId}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: '100%' }}
            >
              {list.filter(c => c.id !== mergeSecondary?.id).map(c => (
                <Select.Option key={c.id} value={c.id}>
                  {c.nickname || c.phone || `用户#${c.id}`}
                  {c.phone ? ` (${c.phone})` : ''}
                  {c.hasAccount ? ' [已绑定用户名]' : ''}
                  {c.vip_name ? ` [${c.vip_name}]` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ==================== 编辑客户信息弹窗 ==================== */}
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#1677ff' }} />
            编辑客户信息
            {editCustomer && (
              <Tag>{editCustomer.nickname || editCustomer.phone || `ID:${editCustomer.id}`}</Tag>
            )}
          </Space>
        }
        open={editModalOpen}
        onOk={handleEditSave}
        onCancel={() => { setEditModalOpen(false); editForm.resetFields(); }}
        confirmLoading={editLoading}
        okText="保存修改"
        cancelText="取消"
        destroyOnClose
        width={480}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="nickname" label="昵称（登录时显示的名称）">
            <Input placeholder="客户昵称，如：张先生" maxLength={50} prefix={<UserOutlined />} />
          </Form.Item>
          <Form.Item name="contactName" label="联系人姓名">
            <Input placeholder="联系人真实姓名" maxLength={20} />
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input.TextArea placeholder="客户地址" maxLength={200} rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ==================== 重置密码弹窗 ==================== */}
      <Modal
        title={
          <Space>
            <KeyOutlined style={{ color: '#fa8c16' }} />
            重置客户密码
            {resetPwdCustomer && (
              <Tag>{resetPwdCustomer.nickname || resetPwdCustomer.username || resetPwdCustomer.phone || `ID:${resetPwdCustomer.id}`}</Tag>
            )}
          </Space>
        }
        open={resetPwdModalOpen}
        onOk={handleResetPwd}
        onCancel={() => { setResetPwdModalOpen(false); resetPwdForm.resetFields(); }}
        confirmLoading={resetPwdLoading}
        okText="确认重置"
        cancelText="取消"
        destroyOnClose
        width={420}
      >
        <div style={{
          background: '#fff7e6', border: '1px solid #ffd591',
          borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13,
        }}>
          设置新密码后，客户即可使用
          <strong> {resetPwdCustomer?.username || '用户名'} </strong>
          加新密码登录。
        </div>
        <Form form={resetPwdForm} layout="vertical">
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="输入至少6位新密码" maxLength={32} prefix={<KeyOutlined />} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="再次输入新密码" maxLength={32} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerManage;
